import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { type StoryboardImportPreview } from '../../lib/googleWorkspace';

interface ImportStoryboardModalProps {
  jobId: string;
  documentId: string;
  scriptId: string;
  invitationId?: string;
  isOpen: boolean;
  onClose: () => void;
  onApplied: (scriptContent: string) => void;
}

export function ImportStoryboardModal({
  jobId,
  documentId,
  scriptId,
  invitationId,
  isOpen,
  onClose,
  onApplied,
}: ImportStoryboardModalProps) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<StoryboardImportPreview | null>(null);
  const [editedScript, setEditedScript] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setPreview(null);
      try {
        const res = await api.post<StoryboardImportPreview>(
          `/google-workspace/jobs/${jobId}/import-storyboard/preview`,
          { documentId, invitationId, scriptId }
        );
        if (cancelled) return;
        setPreview(res.data);
        setEditedScript(res.data.proposedScript);
      } catch (err: unknown) {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Failed to preview storyboard import';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, documentId, invitationId, scriptId]);

  const handleApply = async () => {
    if (!editedScript.trim()) return;
    setApplying(true);
    setError(null);
    try {
      await api.post(`/google-workspace/jobs/${jobId}/import-storyboard/apply`, {
        scriptId,
        content: editedScript,
      });
      onApplied(editedScript);
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to apply storyboard import';
      setError(msg);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import storyboard into script"
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            Replaces script content while in draft or revision requested
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleApply()}
              disabled={applying || loading || !editedScript.trim()}
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply to script'}
            </Button>
          </div>
        </div>
      }
    >
      {loading && (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-600">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-sm">Reading Google Slides and formatting script with AI…</p>
        </div>
      )}

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      {preview && !loading && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline" className="gap-1">
              <Sparkles className="h-3 w-3" />
              {preview.source === 'api' ? 'Google API' : 'Demo'}
            </Badge>
            <span className="text-gray-500">{preview.slideCount} slides</span>
            {preview.kolName && <span className="text-gray-600">KOL: {preview.kolName}</span>}
            {preview.demo && (
              <Badge variant="secondary">Demo mode — connect Google OAuth for live import</Badge>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Proposed script (editable)
            </label>
            <textarea
              className="w-full min-h-[280px] rounded-lg border border-gray-300 p-3 text-sm font-mono"
              value={editedScript}
              onChange={(e) => setEditedScript(e.target.value)}
            />
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
              View slide source text
            </summary>
            <pre className="mt-2 p-3 rounded bg-gray-50 text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">
              {preview.sourceText}
            </pre>
          </details>
        </div>
      )}
    </Modal>
  );
}
